import { useSelector } from 'react-redux';

import { VerificationTemplate } from 'dashboard/models/verification-template';
import {
  VerificationTemplateVersion,
  VerificationTemplateVersionSchema,
  VerificationTemplateVersionStatus,
} from 'dashboard/models/verification-template-version';
import { RootState } from 'dashboard/redux/store';

/**
 * @name `useIsEditableVersion`
 *
 * A hook that returns whether the current verification template version being
 * viewed in Verification Template Editor should be editable or not.
 */
export default () => {
  const verificationTemplate = useSelector<RootState, VerificationTemplate>(
    (state) => state.verificationTemplate.verificationTemplate as VerificationTemplate,
  );
  const verificationTemplateVersion = useSelector<RootState, VerificationTemplateVersion>(
    (state) =>
      state.verificationTemplate.verificationTemplateVersion as VerificationTemplateVersion,
  );
  const verificationTemplateVersionSchema = useSelector<
    RootState,
    VerificationTemplateVersionSchema
  >((state) => state.verificationTemplate.schema as VerificationTemplateVersionSchema);
  if (verificationTemplate == null || verificationTemplateVersion == null) {
    return false;
  }

  // Can only edit a verification template version if it has no associated inquiry template versions
  if (verificationTemplateVersionSchema?.['inquiry-template-versions']?.data?.length > 0) {
    return false;
  }

  const {
    'latest-draft-version': latestDraftVersion,
    'latest-published-version': latestPublishedVersion,
  } = verificationTemplate.relationships;

  if (verificationTemplateVersion.attributes.status === VerificationTemplateVersionStatus.Draft) {
    // only permit editing for a draft if it's the latest draft version
    return verificationTemplateVersion.id === latestDraftVersion?.data?.id;
  } else {
    // only permit editing if it's the latest published version and no draft exists
    if (verificationTemplateVersion.id === latestPublishedVersion?.data?.id) {
      return latestDraftVersion?.data?.id == null;
    } else {
      return false;
    }
  }
};
